/**
 * Gestione consenso cookie + Google Consent Mode v2.
 *
 * Il default `denied` per tutti i segnali viene impostato in `index.html` PRIMA
 * del caricamento di GTM (così i tag Google bufferizzano finché l'utente non
 * decide). Qui gestiamo l'aggiornamento del consenso (`consent: update`), la
 * persistenza nel cookie condiviso `ollylife_consent` e un piccolo pub/sub per riaprire il banner dal
 * footer ("Preferenze cookie").
 *
 * Categorie:
 *  • Necessari   → sempre attivi (security_storage, functionality_storage)
 *  • Analitici   → analytics_storage
 *  • Marketing   → ad_storage, ad_user_data, ad_personalization, personalization_storage
 */

export interface ConsentState {
  analytics: boolean
  marketing: boolean
}

export type ConsentSignal = 'granted' | 'denied'

export interface StoredConsent {
  v:          number
  ts:         string
  categories: ConsentState
  signals:    Record<string, ConsentSignal>
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

export const ACCEPT_ALL: ConsentState = { analytics: true, marketing: true }
export const REJECT_ALL: ConsentState = { analytics: false, marketing: false }

/** Mappa le categorie nei segnali Consent Mode v2. */
export function signalsFor(state: ConsentState): Record<string, ConsentSignal> {
  const marketing: ConsentSignal = state.marketing ? 'granted' : 'denied'
  return {
    analytics_storage:       state.analytics ? 'granted' : 'denied',
    ad_storage:              marketing,
    ad_user_data:            marketing,
    ad_personalization:      marketing,
    personalization_storage: marketing,
    functionality_storage:   'granted',
    security_storage:        'granted',
  }
}

/**
 * Persistenza: cookie `ollylife_consent` sul dominio condiviso `.ollylife.com`,
 * così la scelta fatta su ollylife.com vale anche su app.ollylife.com e
 * viceversa (prima era in localStorage, che è per origine: il banner
 * ricompariva passando dal sito all'app). È il cookie tecnico già dichiarato
 * nella Cookie Policy §3 (12 mesi). In locale si omette il domain: un cookie
 * host-only su `localhost` è comunque condiviso fra le porte (5173 / 3000).
 *
 * ⚠ Stesso formato letto dallo script inline che imposta il Consent Mode prima
 * di GTM (`ollylife-app/index.html`, `ollylife-web/src/app/layout.tsx`): se
 * cambi nome, formato o durata, cambiali anche lì e nel file gemello
 * (`ollylife-app/src/lib/consent.ts` ↔ `ollylife-web/src/lib/consent.ts`).
 */
const COOKIE_NAME = 'ollylife_consent'
const COOKIE_MAX_AGE_S = 60 * 60 * 24 * 365 // 12 mesi, come da Cookie Policy
/** Chiave localStorage della versione precedente: letta una volta per migrarla. */
const LEGACY_STORAGE_KEY = 'ollylife_cookie_consent'
const VERSION = 1

interface CookiePayload {
  v:  number
  ts: string
  a:  0 | 1   // analitici
  m:  0 | 1   // marketing
}

function domainAttr(): string {
  return window.location.hostname.endsWith('ollylife.com') ? '; domain=.ollylife.com' : ''
}

function readCookie(): StoredConsent | null {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`))
  if (!m || !m[1]) return null
  try {
    const p = JSON.parse(decodeURIComponent(m[1])) as CookiePayload
    if (!p || p.v !== VERSION) return null
    const categories = { analytics: p.a === 1, marketing: p.m === 1 }
    return { v: VERSION, ts: p.ts, categories, signals: signalsFor(categories) }
  } catch {
    return null
  }
}

function writeCookie(stored: StoredConsent): void {
  const payload: CookiePayload = {
    v:  VERSION,
    ts: stored.ts,
    a:  stored.categories.analytics ? 1 : 0,
    m:  stored.categories.marketing ? 1 : 0,
  }
  const secure = window.location.protocol === 'https:' ? '; secure' : ''
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(payload))}`
    + `; path=/; max-age=${COOKIE_MAX_AGE_S}; samesite=lax` + domainAttr() + secure
}

/** Scelta salvata in localStorage dalla versione precedente, se ancora entro i 12 mesi. */
function readLegacy(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredConsent
    if (!parsed || parsed.v !== VERSION || !parsed.categories) return null
    const age = Date.now() - new Date(parsed.ts).getTime()
    if (!(age >= 0 && age < COOKIE_MAX_AGE_S * 1000)) return null
    return { ...parsed, signals: signalsFor(parsed.categories) }
  } catch {
    return null
  }
}

function dropLegacy(): void {
  try { localStorage.removeItem(LEGACY_STORAGE_KEY) } catch { /* storage non disponibile */ }
}

export function getStoredConsent(): StoredConsent | null {
  if (typeof document === 'undefined') return null
  const fromCookie = readCookie()
  if (fromCookie) return fromCookie
  // Migrazione una tantum: chi aveva già scelto non rivede il banner.
  const legacy = readLegacy()
  dropLegacy()
  if (legacy) writeCookie(legacy)
  return legacy
}

/** True se l'utente ha già espresso una scelta (valida per la versione corrente). */
export function hasConsentDecision(): boolean {
  return getStoredConsent() !== null
}

/** Salva la scelta, aggiorna Consent Mode e notifica GTM via dataLayer. */
export function saveConsent(state: ConsentState): StoredConsent {
  const signals = signalsFor(state)
  const stored: StoredConsent = { v: VERSION, ts: new Date().toISOString(), categories: state, signals }
  try {
    writeCookie(stored)
  } catch {
    // cookie non scrivibili: il consenso vale per la pagina corrente
  }
  dropLegacy()
  window.gtag?.('consent', 'update', signals)
  window.dataLayer?.push({ event: 'cookie_consent_update', consent: state })
  consentListeners.forEach((l) => l(state))
  return stored
}

// ── Pub/sub per riaprire il banner dal footer ────────────────────────────────
type Listener = () => void
const listeners = new Set<Listener>()

export function openCookiePreferences(): void {
  listeners.forEach((l) => l())
}

export function onOpenCookiePreferences(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// Pub/sub per chi reagisce al cambio di consenso (es. lib/visitor.ts: crea o
// cancella i cookie del tracciamento CRM quando il marketing cambia).
type ConsentListener = (state: ConsentState) => void
const consentListeners = new Set<ConsentListener>()
export function onConsentChange(listener: ConsentListener): () => void {
  consentListeners.add(listener)
  return () => { consentListeners.delete(listener) }
}
